import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Signal, Wifi } from "lucide-react";

/**
 * Realistic iPhone-style mockup used beside the Bundle selector.
 * - Fixed 10:20 aspect ratio so it never looks squashed, regardless of its
 *   parent container's height.
 * - Rolling status-bar clock, dynamic island, side buttons, home indicator.
 * - Controlled: parent owns `selectedId` + `onSelect`. Tapping a row inside
 *   this phone also pulses the matching pin on the map (via shared state).
 */
const TrackerPhone = ({ items, selectedId, onSelect, accent, platformShort, className = "", testIdSuffix = "" }) => {
  const [clock, setClock] = useState(() => {
    const d = new Date();
    const h = d.getHours() % 12 || 12;
    return `${h}:${d.getMinutes().toString().padStart(2, "0")}`;
  });
  useEffect(() => {
    const t = setInterval(() => {
      const d = new Date();
      const h = d.getHours() % 12 || 12;
      setClock(`${h}:${d.getMinutes().toString().padStart(2, "0")}`);
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid={testIdSuffix ? `tracker-phone-mockup-${testIdSuffix}` : "tracker-phone-mockup"}
      className={`relative mx-auto aspect-[10/20] w-full max-w-[300px] ${className}`}
    >
      {/* Phone bezel — thick dark frame with realistic rounding + glossy shadow */}
      <div className="relative h-full w-full rounded-[2.6rem] bg-tracksy-ink p-[7px] shadow-[0_40px_80px_-25px_rgba(0,0,0,0.55),0_18px_30px_-14px_rgba(0,0,0,0.45)]">
        {/* Screen */}
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[2.1rem] bg-gradient-to-b from-white to-[#F6F3EE]">
          {/* Dynamic island + status bar */}
          <div className="relative flex items-center justify-between px-5 pt-3 pb-1.5 text-[10px] font-bold text-tracksy-ink">
            <span>{clock}</span>
            <div className="pointer-events-none absolute left-1/2 top-1.5 h-5 w-20 -translate-x-1/2 rounded-full bg-tracksy-ink" />
            <div className="flex items-center gap-1 text-tracksy-ink/80">
              <Signal className="h-3 w-3" strokeWidth={2.6} />
              <Wifi className="h-3 w-3" strokeWidth={2.6} />
              <span className="ml-0.5 inline-block h-2.5 w-4 rounded-[3px] border border-tracksy-ink/70 bg-tracksy-ink/85" />
            </div>
          </div>

          {/* App header */}
          <div className="flex items-end justify-between px-5 pt-4 pb-2.5">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-tracksy-sub">{platformShort}</p>
              <p className="font-heading text-base font-extrabold leading-tight text-tracksy-ink">My Items</p>
              <p className="mt-0.5 text-[10px] font-semibold text-tracksy-sub">{items.length} nearby</p>
            </div>
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-white"
              style={{ background: accent }}
            >
              <span className="h-1 w-1 animate-pulse rounded-full bg-white" />
              Live
            </span>
          </div>

          {/* Hairline */}
          <div className="mx-5 h-px bg-black/5" />

          {/* Item list */}
          <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto px-3 py-2">
            {items.map((a, i) => {
              const Icon = a.icon;
              const isSelected = selectedId === a.id;
              return (
                <motion.button
                  key={a.id}
                  type="button"
                  data-testid={testIdSuffix ? `phone-item-${a.id}-${testIdSuffix}` : `phone-item-${a.id}`}
                  onClick={() => onSelect(a.id)}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.3 }}
                  whileTap={{ scale: 0.97 }}
                  className={`group mb-0.5 flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors ${
                    isSelected ? "bg-black/[0.04]" : "hover:bg-black/[0.03]"
                  }`}
                  style={isSelected ? { boxShadow: `inset 0 0 0 1.5px ${accent}` } : undefined}
                >
                  <span
                    className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
                    style={{ background: accent }}
                  >
                    <Icon className="relative z-10 h-4 w-4" strokeWidth={2.4} />
                    {isSelected && (
                      <>
                        <motion.span
                          key="phone-ring-1"
                          initial={{ opacity: 0, scale: 1 }}
                          animate={{ opacity: [0, 0.6, 0], scale: [1, 1.5, 2.2] }}
                          transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", times: [0, 0.35, 1] }}
                          className="pointer-events-none absolute inset-0 rounded-full border"
                          style={{ borderColor: accent, borderWidth: 1.5 }}
                        />
                        <motion.span
                          key="phone-ring-2"
                          initial={{ opacity: 0, scale: 1 }}
                          animate={{ opacity: [0, 0.6, 0], scale: [1, 1.5, 2.2] }}
                          transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", times: [0, 0.35, 1], delay: 1.2 }}
                          className="pointer-events-none absolute inset-0 rounded-full border"
                          style={{ borderColor: accent, borderWidth: 1.5 }}
                        />
                      </>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-bold leading-tight text-tracksy-ink">{a.label}</span>
                    <span className="block truncate text-[10px] font-semibold leading-tight text-tracksy-sub">
                      {a.place} · {isSelected ? "now" : `${i + 2}m`}
                    </span>
                  </span>
                  <ChevronRight
                    className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5"
                    style={{ color: isSelected ? accent : "#9A8E78" }}
                  />
                </motion.button>
              );
            })}
          </div>

          {/* Home indicator */}
          <div className="flex justify-center pb-2 pt-1">
            <span className="h-1 w-20 rounded-full bg-tracksy-ink/80" />
          </div>
        </div>
      </div>

      {/* Side buttons */}
      <span className="absolute -left-[2px] top-24 h-14 w-[3px] rounded-l-full bg-tracksy-ink/90" />
      <span className="absolute -right-[2px] top-28 h-20 w-[3px] rounded-r-full bg-tracksy-ink/90" />
    </div>
  );
};

export default TrackerPhone;
